import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { NotificationPanel } from '../notifications/NotificationPanel';
import { HistoryPanel } from '../history/HistoryPanel';
import { useAppShell } from '../../contexts/AppShellContext';
import { useAuth } from '../../contexts/AuthContext';
import { teamLabel } from '../../utils/rbac';
import { useEscalationNotifications } from '../../utils/useEscalationNotifications';
import betanxtWhiteLogo from '../../assets/betanxt-white-logo.png';
import { DemoGuide } from '../DemoGuide';
import { FeedbackButton } from '../feedback/FeedbackButton';


const ROUTE_LABELS = {
  '/ace':           'Ace',
  '/history':       'History',
  '/receiving-log': 'Receiving Log',
  '/all-receipts':  'All Receipts',
  '/jobs':          'All Jobs',
  '/exceptions':    'Exceptions',
  '/escalations':   'Escalations',
  '/follow-ups':    'Follow Ups',
  '/overs':         'Overs',
  '/intake-queue':  'Intake Queue',
  '/proxy':         'Intake Queue',
  '/proxy/exceptions':   'Exceptions',
  '/proxy/escalations':  'Escalations',
  '/proxy/follow-ups':   'Follow Ups',
  '/email-inbox':        'Email Inbox',
  '/email-inbox/fulfillment': 'Email Inbox',
  '/proxy/completed':    'Completed',
  '/proxy/reports':      'Reports',
};

const ROUTE_PARENTS = {
  '/exceptions':                  'Intake Queue',
  '/escalations':                 'Intake Queue',
  '/follow-ups':                  'Intake Queue',
  '/overs':                       'Intake Queue',
  '/proxy/exceptions':            'Intake Queue',
  '/proxy/escalations':           'Intake Queue',
  '/proxy/follow-ups':            'Intake Queue',
};

// Route → app section, used to keep Ace's landing screen (pages/AcePage.tsx)
// in sync with wherever the user actually is — /ace itself has no section of
// its own, so AppShellContext.activeSection is "sticky": it only updates when
// the route unambiguously belongs to one section, and holds its last value on
// neutral pages (/ace, /history, /profile, /workflows, /analytics/*, /admin/*).
const FULFILLMENT_PREFIXES = [
  '/receiving-log', '/production-log', '/inventory', '/all-receipts', '/jobs',
  '/exceptions', '/escalations', '/follow-ups', '/overs', '/intake-queue',
  '/email-intake', '/advisor-support',
];

function detectSection(pathname) {
  if (pathname === '/proxy' || pathname.startsWith('/proxy/')) return 'proxy';
  if (FULFILLMENT_PREFIXES.some(p => pathname === p || pathname.startsWith(p + '/'))) return 'fulfillment';
  return null;
}

function getBreadcrumbs(pathname) {
  const base = pathname.replace(/\/$/, '') || '/';
  const crumbs = [];
  if (ROUTE_PARENTS[base]) crumbs.push(ROUTE_PARENTS[base]);
  if (ROUTE_LABELS[base]) crumbs.push(ROUTE_LABELS[base]);
  return crumbs;
}

export function AppShell() {
  const [notifOpen, setNotifOpen]       = useState(false);
  // The demo guide opens and closes the bell with these events.
  useEffect(() => {
    const show = () => { setHistOpen(false); setNotifOpen(true); };
    const hide = () => setNotifOpen(false);
    window.addEventListener('ace:open-notifications', show);
    window.addEventListener('ace:close-notifications', hide);
    return () => { window.removeEventListener('ace:open-notifications', show); window.removeEventListener('ace:close-notifications', hide); };
  }, []);
  const [histOpen, setHistOpen]         = useState(false);
  const [qaOpen, setQaOpen]             = useState(false);
  const { notifications, unreadCount, markAllRead, markTeamMessageRead, markItemRead } = useEscalationNotifications();
  const [userDropOpen, setUserDropOpen] = useState(false);
  const [toast, setToast]               = useState({ visible: false, msg: '' });
  const toastTimer                      = useRef(null);
  const dropRef                         = useRef(null);
  const qaRef                           = useRef(null);
  const navigate                        = useNavigate();
  const location                        = useLocation();
  const { logout, user }                = useAuth();
  const { toggleSidebar, quickActions, setActiveSection } = useAppShell();
  const breadcrumbs                     = getBreadcrumbs(location.pathname);
  // Pages with no breadcrumb fall back to their last URL segment ("/proxy/deadlines" → "Deadlines").
  const feedbackPage = breadcrumbs.join(' › ')
    || (location.pathname.split('/').filter(Boolean).pop() || 'Home').replace(/-/g, ' ').replace(/^\w/, c => c.toUpperCase());

  useEffect(() => {
    const section = detectSection(location.pathname);
    if (section) setActiveSection(section);
  }, [location.pathname, setActiveSection]);

  const getInitials = (name = '') =>
    name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const AVATAR_COLOR = '#1ABC9D';

  useEffect(() => {
    function handleClick(e) {
      if (dropRef.current && !dropRef.current.contains(e.target)) setUserDropOpen(false);
      if (qaRef.current && !qaRef.current.contains(e.target)) setQaOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleQuickAction = useCallback((key) => {
    setQaOpen(false);
    const handler = quickActions[key];
    if (handler) {
      handler();
    } else {
      const stateMap = { camera: { triggerCamera: true }, upload: { triggerUpload: true } };
      navigate('/ace', { state: stateMap[key] });
    }
  }, [quickActions, navigate]);

  const showToast = useCallback((msg) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ visible: true, msg });
    toastTimer.current = setTimeout(() => setToast({ visible: false, msg: '' }), 3500);
  }, []);

  const handleMarkAllRead = useCallback(() => {
    markAllRead();
    showToast('All notifications marked as read');
  }, [markAllRead, showToast]);

  const outletCtx = { showToast };

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-white">
      {/* Unified top bar */}
      <div className="h-14 flex items-center px-4 gap-3 flex-shrink-0" style={{ background: '#043243', boxShadow: '0 2px 12px rgba(4,50,67,0.35)' }}>
        {/* Hamburger */}
        <button
          onClick={toggleSidebar}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-white/70 hover:bg-white/10 hover:text-white transition-all flex-shrink-0"
          aria-label="Toggle sidebar"
          title="Toggle menu"
        >
          <span className="flex flex-col justify-center gap-[2.5px]">
            <span className="block w-[13px] h-[1.5px] bg-current rounded-sm" />
            <span className="block w-[13px] h-[1.5px] bg-current rounded-sm" />
            <span className="block w-[13px] h-[1.5px] bg-current rounded-sm" />
          </span>
        </button>

        {/* Logo */}
        <img src={betanxtWhiteLogo} alt="BetaNXT" className="h-7 object-contain flex-shrink-0" />
        <span className="text-white/25 text-sm font-light select-none">|</span>

        {/* Breadcrumb */}
        <nav className="flex items-center gap-1 min-w-0">
          <span className="text-xs font-medium text-white/50 flex-shrink-0 tracking-wide">Ace</span>
          {breadcrumbs.map((crumb, i) => (
            <React.Fragment key={crumb}>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className="flex-shrink-0 text-white/25">
                <path d="M3 1.5l3.5 3.5L3 8.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className={`text-xs truncate ${i === breadcrumbs.length - 1 ? 'font-semibold text-white/90' : 'text-white/45 font-medium'}`}>
                {crumb}
              </span>
            </React.Fragment>
          ))}
        </nav>

        {/* Right side */}
        <div className="ml-auto flex items-center gap-2 flex-shrink-0">
          <FeedbackButton pageLabel={feedbackPage} />

          {/* Quick Actions */}
          <div className="relative mr-1" ref={qaRef}>
            <button
              onClick={() => setQaOpen(v => !v)}
              className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all flex-shrink-0"
              title="Quick Actions"
            >
              <span className="w-[21px] h-[21px] rounded-full flex items-center justify-center bg-gradient-to-br from-teal-500 to-cyan-500 pointer-events-none">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M7 2v10M2 7h10" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
              </span>
            </button>

            {qaOpen && (
              <div className="absolute top-[calc(100%+8px)] right-0 bg-white border border-trim rounded-xl overflow-hidden z-50" style={{ minWidth: 192, boxShadow: '0 8px 24px rgba(4,50,67,0.12)' }}>
                <div className="px-4 py-2.5 border-b border-trim text-xs font-semibold text-muted uppercase tracking-wide">Quick Actions</div>
                {[
                  {
                    key: 'camera', label: 'Camera Capture',
                    icon: <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M5.5 2.5h5l1 1.5H14a.5.5 0 0 1 .5.5v8a.5.5 0 0 1-.5.5H2a.5.5 0 0 1-.5-.5V4.5A.5.5 0 0 1 2 4h2.5l1-1.5z" stroke="white" strokeWidth="1.3" strokeLinejoin="round"/><circle cx="8" cy="8" r="2" stroke="white" strokeWidth="1.3"/></svg>,
                  },
                  {
                    key: 'upload', label: 'Upload Scan',
                    icon: <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M8 10.5V3.5M5 6l3-3 3 3" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M2.5 12.5h11" stroke="white" strokeWidth="1.4" strokeLinecap="round"/></svg>,
                  },
                ].map(({ key, label, icon }) => (
                  <button
                    key={key}
                    onClick={() => handleQuickAction(key)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#F5F9FF] transition-colors"
                  >
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-gradient-to-br from-teal-500 to-cyan-500 flex-shrink-0">
                      {icon}
                    </div>
                    <span className="text-sm text-ink font-medium">{label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Notification bell */}
          <button
            onClick={() => { setHistOpen(false); setNotifOpen(v => !v); }}
            className="relative w-9 h-9 rounded-lg flex items-center justify-center text-white/60 hover:bg-white/10 hover:text-white transition-all"
            title="Notifications"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 1.5a5 5 0 00-5 5v3l-1.5 2h13L13 9.5v-3a5 5 0 00-5-5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
              <path d="M6.5 13.5a1.5 1.5 0 003 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#e7000b] border-2 border-[#043243] flex items-center justify-center text-[10px] font-bold text-white leading-none">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* Chat history */}
          <button
            onClick={() => { setNotifOpen(false); setHistOpen(v => !v); }}
            className="relative w-9 h-9 -ml-[3px] rounded-lg flex items-center justify-center text-white/60 hover:bg-white/10 hover:text-white transition-all"
            title="Chat history"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M8 5.2v3.2l2.2 1.3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {/* User profile */}
          <div className="relative" ref={dropRef}>
            <button
              className="flex items-center gap-2 pl-1.5 pr-2.5 py-1 rounded-full border border-white/15 hover:bg-white/10 transition-all cursor-pointer"
              onClick={() => setUserDropOpen(v => !v)}
              title={user?.capabilities?.length
                ? `${user.role}${user.team_id ? ` @ ${teamLabel(user.team_id)}` : ''} — capabilities: ${user.capabilities.join(', ')}`
                : undefined}
            >
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                style={{ background: AVATAR_COLOR }}
              >
                {getInitials(user?.name)}
              </div>
              <div className="flex flex-col items-start leading-tight">
                <span className="text-xs font-semibold text-white/90 leading-none">{user?.name?.split(' ')[0] || 'User'}</span>
              </div>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="text-white/40">
                <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
            </button>

            {userDropOpen && (
              <div className="absolute top-[calc(100%+8px)] right-0 bg-white border border-trim rounded-xl shadow-panel min-w-[240px] z-50 overflow-hidden">
                {/* Identity header */}
                <div className="px-4 py-3 flex items-center gap-2.5">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                    style={{ background: AVATAR_COLOR }}
                  >
                    {getInitials(user?.name)}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-ink truncate">{user?.name}</div>
                  </div>
                </div>
                {/* Profile */}
                <button
                  className="w-full flex items-center px-4 py-2.5 text-left text-sm text-ink hover:bg-tint transition-colors"
                  onClick={() => { setUserDropOpen(false); navigate('/profile'); }}
                >
                  Profile
                </button>
                {/* Sign out */}
                <button
                  className="w-full text-left px-4 py-2.5 text-sm text-copy hover:text-[#e7000b] transition-colors"
                  onClick={async () => { await logout(); navigate('/login'); }}
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sidebar + main content */}
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        <main className="flex-1 overflow-hidden">
          <Outlet context={outletCtx} />
        </main>
      </div>

      {/* Notification panel */}
      <NotificationPanel
        open={notifOpen}
        onClose={() => setNotifOpen(false)}
        onMarkAllRead={handleMarkAllRead}
        onTeamMessageOpened={markTeamMessageRead}
        onItemRead={markItemRead}
        notifications={notifications}
        unreadCount={unreadCount}
      />

      <HistoryPanel open={histOpen} onClose={() => setHistOpen(false)} />


      <DemoGuide />

      <div className={`toast ${toast.visible ? 'show' : ''}`}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <circle cx="7" cy="7" r="6" fill="#14b8a6"/>
          <path d="M4 7l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>{toast.msg}</span>
      </div>
    </div>
  );
}
